<template>
  <v-app>
    <v-navigation-drawer rail permanent :border="false" color="secondary">
      <v-list density="compact" nav>
        <v-tooltip v-for="item in visibleNavigationItems" :key="item.value">
          {{ item.label }}
          <template v-slot:activator="{ props }">
            <v-list-item
              class="nav-element"
              v-bind="props"
              :value="item.value"
              :active="selectedItem === item.value"
              active-class="active-nav-element"
              @click="selectNavigationItem(item)"
            >
              <v-icon :icon="item.icon" />
            </v-list-item>
          </template>
        </v-tooltip>
      </v-list>
      <template v-slot:append>
        <v-list density="compact" nav>
          <v-tooltip>
            {{ $t('COMMON.NAVIGATION.HELP') }}
            <template v-slot:activator="{ props }">
              <v-list-item class="nav-element" v-bind="props" value="help" @click="openHelp">
                <v-icon icon="mdi-account-question-outline" />
              </v-list-item>
            </template>
          </v-tooltip>

          <v-tooltip>
            {{ $t('COMMON.NAVIGATION.LOGOUT') }}
            <template v-slot:activator="{ props }">
              <v-list-item class="nav-element" v-bind="props" @click="logout()" value="exit">
                <v-icon icon="mdi-exit-to-app" />
              </v-list-item>
            </template>
          </v-tooltip>
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-app-bar v-if="!hideHeader">
      <div class="logo-wrapper">
        <img :alt="logo ? 'logo Network' : 'logo Boomerang'" class="logo" :src="appBarLogo" />
      </div>
      <template v-if="displayStoreInfo">
        <p class="store-name">
          {{ repairFileStoreSelected() }}
        </p>
      </template>

      <v-spacer />

      <template v-if="displayCollaboratorInfo">
        <div class="app-bar-actions">
          <NotificationBell />

          <v-tooltip location="bottom">
            {{ $t('COMMON.NAVIGATION.PROFILE') }}
            <template v-slot:activator="{ props }">
              <v-btn-icon
                class="nav-btn"
                v-bind="props"
                icon="mdi-account-outline"
                :color="profileActive ? 'primary' : 'black'"
                variant="tonal"
                @click="selectProfile"
              />
            </template>
          </v-tooltip>
        </div>

        <button
          ref="selectedCollaborator"
          class="selected-collaborator"
          type="button"
          :disabled="hideCollaboratorMenu"
          @click="showCollaborators = !showCollaborators"
        >
          <span class="d-flex flex-column align-start">
            <span class="selected-collaborator-name">
              {{ currentCollaborator.firstname + ' ' + currentCollaborator.lastname }}
            </span>
            <span class="selected-collaborator-email">{{ getEmailFromAccount() }}</span>
          </span>
          <v-icon
            v-if="!hideCollaboratorMenu"
            :icon="showCollaborators ? 'mdi-chevron-down' : 'mdi-chevron-up'"
          />
        </button>

        <div v-if="showCollaborators" class="collaborators-list" :style="{ minWidth: minWidth }">
          <ul>
            <li
              v-for="collaborator of getCollaboratorList"
              :key="collaborator.technical_id"
              :class="{ active: getActiveCollaborator(collaborator.technical_id) }"
              @click="selectCollaborator(collaborator)"
            >
              {{ collaborator.firstname + ' ' + collaborator.lastname }}
              <v-icon v-if="getActiveCollaborator(collaborator.technical_id)" icon="mdi-check" />
            </li>
          </ul>
        </div>
      </template>
    </v-app-bar>

    <v-main class="main">
      <slot />
    </v-main>

    <DashboardFooter v-if="$route.name === 'dashboard'" />

    <RightNavigationDrawer
      :currentRepairFile="repairDrawerStore.payload.currentRepairFile"
      :rentalFileId="repairDrawerStore.payload.rentalFileId"
      :refreshCurrentRepairFile="repairDrawerStore.payload.refreshCurrentRepairFile"
      :newActionComponentProps="repairDrawerStore.isOpen"
      :openDeliveryClientDrawer="repairDrawerStore.openDeliveryClientDrawer"
      @updateCurrentRepairFile="handleRepairDrawerUpdateCurrentRepairFile"
      @close="repairDrawerStore.closeDrawer()"
      @showGuaranteeExtensionBoomerangModal="handleRepairDrawerShowGuaranteeExtensionModal"
      @openRentalWarningModal="handleRepairDrawerOpenRentalWarningModal"
      @reset-delivery-client-state="repairDrawerStore.resetDeliveryClientState()"
    />
  </v-app>
</template>

<script>
import { handleResponseError } from '@/utils/toastUtils'
import { useAuthStore } from '@/store/authStore'
import { useRepairDrawerStore } from '@/store/repairDrawerStore'
import NotificationBell from '@/components/header/NotificationBell.vue'
import DashboardFooter from '@/components/dashboard/DashboardFooter.vue'
import RightNavigationDrawer from '@/components/repair-files/right-drawer/RightNavigationDrawer.vue'
import { checkPermission } from '@/utils/permissionUtils'
import CollaboratorService from '@/services/CollaboratorService'
import { useZendeskChat } from '@/composables/useZendeskChat'
import boomerangBlackLogo from '@/assets/images/boomerang_black_logo.png'

const { mount: mountZendesk, unmount: unmountZendesk, show: showZendesk } = useZendeskChat()

export default {
  name: 'DistributorLayout',
  components: { DashboardFooter, NotificationBell, RightNavigationDrawer },
  data() {
    const authStore = useAuthStore()
    const repairDrawerStore = useRepairDrawerStore()
    return {
      authStore,
      repairDrawerStore,
      selectedItem: 'dashboard',
      showCollaborators: false,
      minWidth: '0px',
      zendeskUrl: window.APP_CONFIG.VITE_ZENDESK_URL,
      profileActive: false,
      boomerangBlackLogo,
      navigationItems: [
        {
          value: 'dashboard',
          label: this.$t('COMMON.NAVIGATION.DASHBOARD'),
          icon: 'mdi-home',
          routeName: 'dashboard',
          permissions: []
        },
        {
          value: 'repairFiles',
          label: this.$t('COMMON.NAVIGATION.REPAIR_FILES'),
          icon: 'mdi-tag-outline',
          routeName: 'repairFiles',
          permissions: ['DISTRIBUTOR_VIEW_ALL_SUMMARY_REPAIR_FILE']
        },
        {
          value: 'users',
          label: this.$t('COMMON.NAVIGATION.USERS'),
          icon: 'mdi-account-multiple',
          routeName: 'users',
          permissions: ['DISTRIBUTOR_ACCOUNT_READ_PERMISSION']
        },
        {
          value: 'environmentalPerformanceReport',
          label: this.$t('COMMON.NAVIGATION.ENVIRONMENTAL_PERFORMANCE_REPORT'),
          icon: 'mdi-leaf',
          routeName: 'environmentalPerformanceReport',
          permissions: []
        }
      ],
      logo: null
    }
  },
  watch: {
    $route: {
      handler: function (params) {
        this.selectedItem = this.$route.meta.menuSelected
        this.profileActive = params.fullPath.startsWith('/profile')
      },
      deep: true,
      immediate: true
    },
    showCollaborators(bool) {
      if (bool) {
        const selectedCollaborator =
          this.$refs.selectedCollaborator?.$el || this.$refs.selectedCollaborator
        this.minWidth = (selectedCollaborator?.clientWidth || 0) + 1 + 'px'
      }
    }
  },
  computed: {
    currentCollaborator() {
      return this.authStore.getCurrentCollaborator || {}
    },
    getCollaboratorList() {
      return this.authStore.getCollaboratorList
    },
    appBarLogo() {
      return this.logo || this.boomerangBlackLogo
    },
    visibleNavigationItems() {
      return this.navigationItems.filter(
        (item) => item.permissions.length === 0 || this.checkPermission(item.permissions)
      )
    },
    displayCollaboratorInfo() {
      return (
        this.$route.name !== 'repairFileCreate' &&
        this.$route.name !== 'repairFileShipping' &&
        this.$route.name !== 'repairFileFinalization'
      )
    },
    displayStoreInfo() {
      return (
        this.$route.name === 'repairFileCreate' ||
        this.$route.name === 'repairFileShipping' ||
        this.$route.name === 'repairFileFinalization'
      )
    },
    hideCollaboratorMenu() {
      return this.getCollaboratorList.length <= 1
    },
    hideHeader() {
      return this.$route.meta.hideHeader === true
    }
  },
  mounted() {
    mountZendesk()
    showZendesk()
  },
  beforeUnmount() {
    unmountZendesk()
  },
  created() {
    const connectedUserStoreList = this.authStore.getStoreListCollaborator
    if (connectedUserStoreList.length > 1) {
      this.logo = this.authStore.getNetworkLogo
    } else if (
      connectedUserStoreList.length === 1 &&
      connectedUserStoreList[0].logo_path !== null
    ) {
      this.logo = connectedUserStoreList[0].logo_path
    } else {
      this.logo = null
    }
  },
  methods: {
    checkPermission,
    handleRepairDrawerUpdateCurrentRepairFile() {
      const callback = this.repairDrawerStore.payload.onUpdateCurrentRepairFile
      if (typeof callback === 'function') callback()
    },
    handleRepairDrawerShowGuaranteeExtensionModal() {
      const callback = this.repairDrawerStore.payload.onShowGuaranteeExtensionBoomerangModal
      if (typeof callback === 'function') callback()
    },
    handleRepairDrawerOpenRentalWarningModal() {
      const callback = this.repairDrawerStore.payload.onOpenRentalWarningModal
      if (typeof callback === 'function') callback()
    },
    async logout() {
      this.loading = true

      try {
        await this.authStore.logoutAction()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        await this.$router.push({ name: 'login' })
      }
    },
    selectNavigationItem(item) {
      this.selectedItem = item.value
      this.$router.push({ name: item.routeName })
    },
    openHelp() {
      if (!this.zendeskUrl) return

      window.open(this.zendeskUrl, '_blank', 'noopener,noreferrer')
    },
    selectProfile() {
      this.$router.push({ name: 'profile' })
    },
    getEmailFromAccount() {
      return this.authStore.userDetail.email
    },
    repairFileStoreSelected() {
      return this.authStore.getRepairFileStoreSelected?.name
    },
    async selectCollaborator(collaborator) {
      try {
        await CollaboratorService.switchCollaborator(collaborator.technical_id)

        this.showCollaborators = false
        this.authStore.selectedCollaborator(collaborator.technical_id)
      } catch (error) {
        handleResponseError(error)
      }
    },
    getActiveCollaborator(collaboratorId) {
      return this.currentCollaborator.technical_id === parseInt(collaboratorId)
    }
  }
}
</script>

<style>
.v-navigation-drawer__content::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px #5d5d5d;
}

.v-navigation-drawer__content::-webkit-scrollbar {
  width: 0;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb {
  -webkit-box-shadow: inset 0 0 6px #424242;
}

.logo-wrapper {
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

.logo {
  max-width: 600px;
  max-height: 48px;
  object-fit: contain;
}

.main {
  background-color: var(--default-background-color);
}

.nav-element,
.active-nav-element,
.nav-element.v-list-item--active,
.nav-element .v-list-item__overlay,
.nav-element .v-ripple__container,
.active-nav-element .v-list-item__overlay,
.active-nav-element .v-ripple__container {
  border-radius: var(--default-radius);
}

.nav-element,
.active-nav-element,
.nav-element.v-list-item--active {
  overflow: hidden;
}

.nav-element.v-list-item--active,
.active-nav-element {
  background-color: rgb(var(--v-theme-surface));
}

.nav-element.v-list-item--active .v-list-item__overlay,
.active-nav-element .v-list-item__overlay {
  background-color: transparent;
  opacity: 0;
}

.active-nav-element .v-icon,
.active-nav-element .v-list-item__prepend > .v-icon,
.nav-element.v-list-item--active .v-icon,
.nav-element.v-list-item--active .v-list-item__prepend > .v-icon {
  color: rgb(var(--v-theme-secondary));
}

.app-bar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-collaborator {
  height: 100%;
  display: flex;
  min-width: 250px;
  flex-direction: row;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  justify-content: space-between;
}

.selected-collaborator:hover {
  cursor: pointer;
}

.selected-collaborator:disabled {
  cursor: default;
}

.selected-collaborator-name {
  color: rgb(var(--v-theme-secondary));
  font-weight: bold;
}

.selected-collaborator-email {
  font-size: 13px;
  color: #616161;
}

.collaborators-list {
  right: 0;
  top: 64px;
  z-index: 1010;
  position: fixed;
  width: fit-content;
}

.collaborators-list ul li {
  list-style: none;
  padding: 10px 20px;
  color: rgb(var(--v-theme-secondary));
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  background-color: white;
  border-top: 1px solid var(--vuetify-grey);
  border-left: 1px solid var(--vuetify-grey);
  border-right: 1px solid var(--vuetify-grey);
  border-bottom: unset;
  justify-content: space-between;
}

.collaborators-list ul li:last-child {
  border-bottom: 1px solid var(--vuetify-grey);
}

.collaborators-list ul li:hover {
  background-color: var(--default-background-color);
  cursor: pointer;
}

.collaborators-list ul li.active {
  background-color: #f8f8f8;
  cursor: pointer;
}

.collaborators-list ul li.active:hover {
  background-color: var(--default-background-color);
}

.store-name {
  margin-left: 12px;
}

.v-toolbar {
  box-shadow: 0 0 20px 2px rgba(0, 0, 0, 0.1) !important;
}
</style>
